import React, { useState, useEffect, useCallback } from 'react'
import { Table, Button, Input, Modal, Form, Tag, Space, Select, Alert, Tooltip, DatePicker, Spin, Tabs } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, DownOutlined, RightOutlined } from '@ant-design/icons'
import { apiRequest } from '../../helpers'
import EmailSourceButton from './EmailSourceButton'
import dayjs from 'dayjs'

const apiUrl = process.env.REACT_APP_API
const MONEDE = ['RON', 'EUR', 'USD']
const monedaColor = { RON: 'green', EUR: 'blue', USD: 'cyan' }

const formatPret = (pret, moneda) => {
  if (pret == null) return <span style={{ color: '#aaa' }}>—</span>
  return (
    <>
      <strong>{Number(pret).toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</strong>{' '}
      <Tag color={monedaColor[moneda] || 'default'} style={{ fontSize: 10, marginLeft: 4 }}>{moneda}</Tag>
    </>
  )
}

const TpmOffers = () => {
  const [offers, setOffers] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [filterMoneda, setFilterMoneda] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [meta, setMeta] = useState({ totalItems: 0 })
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedOffer, setSelectedOffer] = useState(null)
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  const fetchOffers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
        sortBy: 'dataOferta:DESC',
        ...(search ? { search } : {}),
        ...(filterMoneda ? { 'filter.moneda': filterMoneda } : {}),
      })
      const res = await apiRequest(`${apiUrl}tpm/offers?${params}`, 'GET')
      if (res?.data?.data) {
        setOffers(res.data.data)
        setMeta({ totalItems: res.data.meta?.totalItems || 0 })
      }
    } catch { setError('Eroare la incarcarea ofertelor.') }
    finally { setLoading(false) }
  }, [page, pageSize, search, filterMoneda])

  useEffect(() => { fetchOffers() }, [fetchOffers])

  const currentPageKeys = offers.map(o => o.id)
  const allCurrentExpanded = currentPageKeys.length > 0 && currentPageKeys.every(k => expandedRowKeys.includes(k))

  const toggleExpandAll = () => {
    if (allCurrentExpanded) {
      setExpandedRowKeys(prev => prev.filter(k => !currentPageKeys.includes(k)))
    } else {
      setExpandedRowKeys(prev => [...new Set([...prev, ...currentPageKeys])])
    }
  }

  const handlePageChange = (p, ps) => {
    setPage(p)
    setPageSize(ps)
    setExpandedRowKeys([])
  }

  const openCreate = () => {
    setSelectedOffer(null)
    form.resetFields()
    form.setFieldsValue({ moneda: 'RON' })
    setModalOpen(true)
  }

  const openEdit = (offer) => {
    setSelectedOffer(offer)
    form.setFieldsValue({
      partNumber: offer.partNumber,
      firma: offer.firma || '',
      email: offer.email || '',
      pret: offer.pret ?? '',
      moneda: offer.moneda || 'RON',
      cantitate: offer.cantitate ?? '',
      dataOferta: offer.dataOferta ? dayjs(offer.dataOferta) : null,
    })
    setModalOpen(true)
  }

  const handleSave = async () => {
    const values = await form.validateFields()
    setSaving(true)
    const payload = {
      ...values,
      pret: values.pret !== '' && values.pret != null ? Number(values.pret) : null,
      cantitate: values.cantitate !== '' && values.cantitate != null ? Number(values.cantitate) : null,
      dataOferta: values.dataOferta ? values.dataOferta.format('YYYY-MM-DD') : null,
    }
    try {
      if (selectedOffer) {
        await apiRequest(`${apiUrl}tpm/offers/${selectedOffer.id}`, 'PATCH', payload)
      } else {
        await apiRequest(`${apiUrl}tpm/offers`, 'POST', payload)
      }
      setModalOpen(false)
      fetchOffers()
    } catch (e) {
      Modal.error({ title: 'Eroare', content: e?.response?.data?.message || 'Eroare la salvare.' })
    } finally { setSaving(false) }
  }

  const handleDelete = async () => {
    try {
      await apiRequest(`${apiUrl}tpm/offers/${selectedOffer.id}`, 'DELETE')
      setDeleteModalOpen(false)
      fetchOffers()
    } catch { Modal.error({ title: 'Eroare', content: 'Eroare la stergere.' }) }
  }

  // Coloane sub-tabel oferte
  const offerSubColumns = [
    { title: 'Firma', dataIndex: 'firma', key: 'firma', render: (v, r) => (
      <div>
        <div>{v || <span style={{ color: '#aaa' }}>—</span>}</div>
        {r.email && <small style={{ color: '#888' }}>{r.email}</small>}
      </div>
    )},
    { title: 'Pret', key: 'pret', render: (_, r) => formatPret(r.pret, r.moneda) },
    { title: 'Cantitate', dataIndex: 'cantitate', key: 'cantitate', render: (v) => v ?? <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Data oferta', dataIndex: 'dataOferta', key: 'dataOferta', render: (v) => v ? new Date(v).toLocaleDateString('ro-RO') : <span style={{ color: '#aaa' }}>—</span> },
  ]

  // Coloane sub-tabel RFQ
  const rfqSubColumns = [
    { title: 'Firma', dataIndex: 'firma', key: 'firma', render: (v, r) => (
      <div>
        <div>{v || <span style={{ color: '#aaa' }}>—</span>}</div>
        {r.email && <small style={{ color: '#888' }}>{r.email}</small>}
      </div>
    )},
    { title: 'Pret', key: 'pret', render: (_, r) => formatPret(r.pret, r.moneda) },
    { title: 'Cantitate', dataIndex: 'cantitate', key: 'cantitate', render: (v) => v ?? <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Observatii', dataIndex: 'observatii', key: 'observatii', render: (v) => v || <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Data', dataIndex: 'dataOferta', key: 'dataOferta', render: (v) => v ? new Date(v).toLocaleDateString('ro-RO') : <span style={{ color: '#aaa' }}>—</span> },
  ]

  // Expand row — exact aceeasi structura ca ExpandedOffers din TpmRfqTab
  const ExpandedDetails = ({ partNumber }) => {
    const [offersData, setOffersData] = React.useState([])
    const [rfqsData, setRfqsData] = React.useState([])
    const [loadingData, setLoadingData] = React.useState(true)
    const [subPageOffers, setSubPageOffers] = React.useState(1)
    const [subPageSizeOffers, setSubPageSizeOffers] = React.useState(10)
    const [subPageRfq, setSubPageRfq] = React.useState(1)
    const [subPageSizeRfq, setSubPageSizeRfq] = React.useState(10)

    React.useEffect(() => {
      Promise.all([
        apiRequest(`${apiUrl}tpm/offers?filter.partNumber=${encodeURIComponent(partNumber)}&limit=200&sortBy=dataOferta:DESC`, 'GET'),
        apiRequest(`${apiUrl}tpm/rfq-items/by-part/${encodeURIComponent(partNumber)}`, 'GET'),
      ])
        .then(([offRes, rfqRes]) => {
          setOffersData(offRes?.data?.data || [])
          setRfqsData(rfqRes?.data || [])
        })
        .catch(() => {})
        .finally(() => setLoadingData(false))
    }, [partNumber])

    if (loadingData) return <Spin size="small" style={{ margin: '8px 16px' }} />

    const tabs = [
      {
        key: 'offers',
        label: `Oferte (${offersData.length})`,
        children: offersData.length === 0
          ? <div style={{ color: '#888', padding: '8px 16px' }}>Nu exista oferte pentru acest articol.</div>
          : <Table
              dataSource={offersData}
              columns={offerSubColumns}
              rowKey="id"
              size="small"
              pagination={offersData.length > subPageSizeOffers ? {
                current: subPageOffers, pageSize: subPageSizeOffers, total: offersData.length,
                onChange: (p, ps) => { setSubPageOffers(p); setSubPageSizeOffers(ps) },
                showSizeChanger: true, pageSizeOptions: [10, 20, 50],
                showTotal: (t) => `${t} oferte`, size: 'small',
              } : false}
              style={{ margin: '0 16px' }}
            />,
      },
      {
        key: 'rfq',
        label: `Cereri de oferta (${rfqsData.length})`,
        children: rfqsData.length === 0
          ? <div style={{ color: '#888', padding: '8px 16px' }}>Nu exista cereri de oferta pentru acest articol.</div>
          : <Table
              dataSource={rfqsData}
              columns={rfqSubColumns}
              rowKey="id"
              size="small"
              pagination={rfqsData.length > subPageSizeRfq ? {
                current: subPageRfq, pageSize: subPageSizeRfq, total: rfqsData.length,
                onChange: (p, ps) => { setSubPageRfq(p); setSubPageSizeRfq(ps) },
                showSizeChanger: true, pageSizeOptions: [10, 20, 50],
                showTotal: (t) => `${t} cereri`, size: 'small',
              } : false}
              style={{ margin: '0 16px' }}
            />,
      },
    ]

    return <Tabs items={tabs} size="small" style={{ margin: '4px 16px' }} />
  }

  const expandedRowRender = (record) => <ExpandedDetails partNumber={record.partNumber} />

  const columns = [
    { title: 'Part Number', dataIndex: 'partNumber', key: 'partNumber', render: (v) => <code>{v}</code> },
    { title: 'Denumire', key: 'denumire', render: (_, r) => r.part?.denumire || <span style={{ color: '#aaa' }}>—</span> },
    {
      title: 'Firma', key: 'firma',
      render: (_, r) => (
        <div>
          <div>{r.firma || <span style={{ color: '#aaa' }}>—</span>}</div>
          {r.email && <small style={{ color: '#888' }}>{r.email}</small>}
        </div>
      )
    },
    { title: 'Pret', key: 'pret', render: (_, r) => formatPret(r.pret, r.moneda) },
    { title: 'Pret EUR', key: 'pretEur', render: (_, r) => r.pretEur != null ? <><strong>{Number(r.pretEur).toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</strong> <Tag color="blue">EUR</Tag></> : <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Cantitate', dataIndex: 'cantitate', key: 'cantitate', render: (v) => v ?? <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Data oferta', dataIndex: 'dataOferta', key: 'dataOferta', render: (v) => v ? new Date(v).toLocaleDateString('ro-RO') : <span style={{ color: '#aaa' }}>—</span> },
    { title: 'Email sursa', key: 'emailMessageId', render: (_, r) => <EmailSourceButton emailMessageId={r.emailMessageId} /> },
    {
      title: 'Actiuni', key: 'actions', width: 100,
      render: (_, r) => (
        <Space>
          <Tooltip title="Editeaza"><Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} /></Tooltip>
          <Tooltip title="Sterge"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => { setSelectedOffer(r); setDeleteModalOpen(true) }} /></Tooltip>
        </Space>
      )
    },
  ]

  return (
    <div>
      <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <strong style={{ fontSize: 16 }}>Oferte Preturi</strong>
          <Space wrap>
            <Input.Search
              placeholder="Cauta part, firma, denumire..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              style={{ width: 260 }}
              allowClear
            />
            <Select
              style={{ width: 110 }}
              value={filterMoneda || undefined}
              onChange={v => { setFilterMoneda(v || ''); setPage(1) }}
              allowClear
              placeholder="Moneda"
            >
              {MONEDE.map(m => <Select.Option key={m} value={m}>{m}</Select.Option>)}
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Adauga oferta</Button>
            <Button icon={<ReloadOutlined />} onClick={fetchOffers} />
          </Space>
        </div>

        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 12 }} />}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <Tooltip title={allCurrentExpanded ? 'Restrânge toate' : 'Expandează toate rândurile din pagina curentă'}>
            <Button size="small" icon={allCurrentExpanded ? <RightOutlined /> : <DownOutlined />} onClick={toggleExpandAll}>
              {allCurrentExpanded ? 'Restrânge toate' : 'Expandează toate'}
            </Button>
          </Tooltip>
        </div>

        <Table
          columns={columns}
          dataSource={offers}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{
            current: page, pageSize, total: meta.totalItems,
            onChange: handlePageChange,
            showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total) => `Total: ${total} oferte`,
          }}
          expandable={{
            expandedRowRender,
            expandedRowKeys,
            onExpand: (expanded, record) => {
              setExpandedRowKeys(prev =>
                expanded ? [...prev, record.id] : prev.filter(k => k !== record.id)
              )
            },
          }}
          locale={{ emptyText: 'Nu exista oferte.' }}
        />
      </div>

      <Modal
        title={selectedOffer ? 'Editeaza oferta' : 'Adauga oferta noua'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText={saving ? 'Se salveaza...' : 'Salveaza'}
        cancelText="Anuleaza"
        width={700}
      >
        <Form form={form} layout="vertical">
          <div className="row g-3">
            <div className="col-md-4">
              <Form.Item label="Part Number" name="partNumber" rules={[{ required: true, message: 'Part Number este obligatoriu' }]}>
                <Input disabled={!!selectedOffer} placeholder="ex: ABC123" />
              </Form.Item>
            </div>
            <div className="col-md-4">
              <Form.Item label="Firma" name="firma">
                <Input placeholder="Numele firmei" />
              </Form.Item>
            </div>
            <div className="col-md-4">
              <Form.Item label="Email contact" name="email">
                <Input placeholder="contact@firma.ro" />
              </Form.Item>
            </div>
            <div className="col-md-3">
              <Form.Item label="Pret" name="pret">
                <Input type="number" step="0.01" placeholder="0.00" />
              </Form.Item>
            </div>
            <div className="col-md-2">
              <Form.Item label="Moneda" name="moneda">
                <Select>
                  {MONEDE.map(m => <Select.Option key={m} value={m}>{m}</Select.Option>)}
                </Select>
              </Form.Item>
            </div>
            <div className="col-md-3">
              <Form.Item label="Cantitate" name="cantitate">
                <Input type="number" placeholder="buc" />
              </Form.Item>
            </div>
            <div className="col-md-4">
              <Form.Item label="Data oferta" name="dataOferta">
                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
              </Form.Item>
            </div>
          </div>
        </Form>
      </Modal>

      <Modal
        title="Confirmare stergere"
        open={deleteModalOpen}
        onOk={handleDelete}
        onCancel={() => setDeleteModalOpen(false)}
        okText="Sterge"
        okButtonProps={{ danger: true }}
        cancelText="Anuleaza"
      >
        <p>Stergi oferta <strong>{selectedOffer?.partNumber}</strong> de la <strong>{selectedOffer?.firma || 'firma necunoscuta'}</strong>?</p>
      </Modal>
    </div>
  )
}

export default TpmOffers
